import React, { useEffect, useRef, useState } from 'react'
import { CButton, CCardBody } from '@coreui/react-pro'
import { path } from '../../../libs'
import { InputRef, Table, Tag } from 'antd'
import { useDemoContestList } from './hooks'
import { FilterValue } from 'antd/es/table/interface'
import { ColumnsType } from 'antd/lib/table'
import CIcon from '@coreui/icons-react'
import { cilPen, cilZoom } from '@coreui/icons'
import { DemoContest } from './types'
import {
  getColumnSearchDateProps,
  getColumnSearchProps,
} from '../../../libs/tableFunctions'
import { Guard } from '../../../features/session/guard'
import { transformCountriesToOptions } from '../../../libs/formatCountries'
import { useUnit } from 'effector-react'
import { $countriesList } from '../../../features/dictionaries/model'
import { currencyFilterList } from '../../billing/const'

export const DemoContestList: React.FC = () => {
  const {
    demoContestList,
    totalCountOfDemoContestList,
    loading,
    onChangeDemoContestList,
  } = useDemoContestList()

  const countriesList = useUnit($countriesList)

  const [searchText, setSearchText] = useState<{
    [key: string]: string
  }>({})
  const [orderBy, setOrderBy] = useState('id')
  const [orderDirection, setOrderDirection] = useState('-')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [filtersState, setFiltersState] = useState<
    Record<string, FilterValue | null>
  >({})

  const searchInput = useRef<InputRef>(null)

  const handleTableChange = ({
    currentPage,
    currentPageSize,
    currentOrderBy,
    currentOrderDirection,
    filters,
  }: {
    currentPage?: number
    currentPageSize?: number
    currentOrderBy?: string
    currentOrderDirection?: string
    filters?: Record<string, FilterValue | null>
  }) => {
    if (currentPage) {
      setPage(currentPage)
    }
    if (currentPageSize) {
      setPageSize(currentPageSize)
    }
    if (currentOrderBy) {
      setOrderBy(currentOrderBy)
    }
    if (currentOrderDirection) {
      if (currentOrderDirection == 'descend') {
        setOrderDirection('-')
      } else {
        setOrderDirection('')
      }
    }

    if (filters) {
      setFiltersState(filters)
    }
  }

  useEffect(() => {
    onChangeDemoContestList({
      limit: pageSize,
      page: page,
      orderBy: orderBy,
      orderDirection: orderDirection,
      searchText: searchText,
      filters: filtersState,
    })
  }, [pageSize, page, orderBy, orderDirection, searchText, filtersState])

  const columns: ColumnsType<DemoContest> = [
    {
      title: 'ID',
      dataIndex: 'id',
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      ...getColumnSearchProps<DemoContest>(
        'name',
        setSearchText,
        searchText,
        searchInput,
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      sorter: true,
    },
    {
      title: 'Type',
      dataIndex: 'contestType',
    },
    {
      title: 'Country',
      dataIndex: 'availableCountries',
      filters: transformCountriesToOptions(countriesList),
      filterSearch: true,
      render: (countries: string[]) => (
        <>
          {countries?.map((c) => (
            <Tag key={c}>{c}</Tag>
          ))}
        </>
      ),
    },
    {
      title: 'Currency',
      dataIndex: 'currency',
      filters: currencyFilterList,
      filterSearch: true,
    },
    {
      title: 'Registered at',
      dataIndex: 'registrationAt',
      ...getColumnSearchDateProps<DemoContest>(
        'registrationAt',
        setSearchText,
        searchText,
        searchInput,
      ),
      sorter: true,
      render: (registrationAt: string, record: DemoContest) =>
        record.humanRegistrationAt,
    },
    {
      title: 'Live at',
      dataIndex: 'activatesAt',
      ...getColumnSearchDateProps<DemoContest>(
        'activatesAt',
        setSearchText,
        searchText,
        searchInput,
      ),
      sorter: true,
      render: (activatesAt: string, record: DemoContest) =>
        record.humanActivatesAt,
    },
    {
      title: 'Ended at',
      dataIndex: 'endsAt',
      ...getColumnSearchDateProps<DemoContest>(
        'endsAt',
        setSearchText,
        searchText,
        searchInput,
      ),
      sorter: true,
      render: (endsAt: string, record: DemoContest) => record.humanEndsAt,
    },
    {
      title: 'Participant',
      dataIndex: 'participantCount',
      sorter: (a, b) => a.participantCount - b.participantCount,
      defaultSortOrder: 'descend',
    },
    {
      title: 'Details',
      fixed: 'right',
      width: 100,
      render: (item) => {
        return (
          <>
            <CButton
              color="secondary"
              className="me-2"
              title="View"
              href={path.promo.demoContest.details(item.id)}
            >
              <CIcon icon={cilZoom} />
            </CButton>
          </>
        )
      },
    },
    {
      title: 'Edit',
      fixed: 'right',
      width: 100,
      render: (item) => {
        return (
          <Guard permission="edit_democontest">
            <CButton
              color="success"
              className="me-2"
              title="View"
              href={path.promo.demoContest.edit(item.id)}
            >
              <CIcon icon={cilPen} />
            </CButton>
          </Guard>
        )
      },
    },
  ]

  return (
    <CCardBody>
      <Guard permission="create_democontest">
        <CButton
          color="primary"
          className="mb-2"
          href={path.promo.demoContest.create()}
        >
          Create demo contest
        </CButton>
      </Guard>
      <Table
        dataSource={demoContestList}
        bordered={true}
        scroll={{ x: true }}
        loading={loading}
        rowKey="demoContestID"
        columns={columns}
        pagination={{
          showSizeChanger: true,
          total: totalCountOfDemoContestList,
        }}
        onChange={(pagination, filters, sorter, extra) =>
          handleTableChange({
            //@ts-ignore
            currentPage: pagination.current, //@ts-ignore
            currentPageSize: pagination.pageSize, //@ts-ignore
            currentOrderDirection: sorter.order, //@ts-ignore
            currentOrderBy: sorter.field,
            filters,
          })
        }
      ></Table>
    </CCardBody>
  )
}
